/**
 * Backend-persisted Risk (ADR 0023): the client renders tiers the server evaluated and stored —
 * it never computes one (the old client formula lived in contractRisk.ts and is gone). A null
 * tier means "Not assessed": the contract has not been through an evaluation trigger yet, and it
 * must never be conflated with LOW_RISK (docs/plans/risk-tier-switchover.md D4).
 *
 * Vocabulary (ADR 0026): tiers are stored and transported as HIGH_RISK / MEDIUM_RISK / LOW_RISK
 * — the enum names the server's `RiskTier.wire()` emits — and displayed "High risk" etc. via
 * `tierLabel`. red/amber/green are PRESENTATION ONLY: {@link tierColorName} is the single mapping
 * from a tier to a colour word, and nothing else in the client may key off colour.
 */
export type RiskTier = 'HIGH_RISK' | 'MEDIUM_RISK' | 'LOW_RISK'
export type RiskDecidedBy = 'BASELINE' | 'POLICY'

/** The presentation colour word a tier renders in (CSS class suffixes, chart palettes). */
export type RiskColorName = 'red' | 'amber' | 'green' | 'unassessed'

/**
 * Why the tier is what it is (ADR 0022/0023: a policy override — especially a downgrade — is
 * never visually silent, and its justification is one click away).
 */
export interface RiskProvenance {
  /** The deterministic baseline's tier before any policy override. */
  baselineTier: RiskTier | null
  /** POLICY only when the final tier differs from the baseline (affirmations stay BASELINE). */
  decidedBy: RiskDecidedBy | null
  /** The risk policy's reasoning for moving the tier; null when the baseline decided. */
  justification: string | null
  /** When risk was last evaluated (trigger-based, never ambient). */
  evaluatedAt: string | null
}

/** A contract's persisted risk as the dashboards consume it. */
export interface ContractRisk {
  tier: RiskTier | null
  reasons: string[]
  provenance: RiskProvenance
}

/** The semantic-layer risk fields, as returned by the fold read / semantic queries. */
export interface SemanticRiskFields {
  riskTier?: string | null
  riskBaselineTier?: string | null
  riskReasons?: string[] | null
  riskDecidedBy?: string | null
  riskJustification?: string | null
  riskEvaluatedAt?: string | null
}

/** Riskiest first — the order {@link riskTierRank} sorts by. */
const TIERS: readonly RiskTier[] = ['HIGH_RISK', 'MEDIUM_RISK', 'LOW_RISK']

/**
 * The colour words tiers were STORED as before ADR 0026. Mirrors `RiskTier.LEGACY_COLOUR_ALIASES`
 * on the server and is a PERMANENT backstop, not a transitional shim: the dev database is
 * restored from dumps that predate the rename (so legacy values reappear forever), and the
 * risk-policy LLM may regress to colour vocabulary. Deleting it is a bug.
 */
const LEGACY_COLOUR_ALIASES: Record<string, RiskTier> = {
  RED: 'HIGH_RISK',
  AMBER: 'MEDIUM_RISK',
  GREEN: 'LOW_RISK',
}

/** Canonical tier token (any case) or a legacy colour word → tier; anything else → null. */
export function asTier(value: string | null | undefined): RiskTier | null {
  if (value == null) return null
  const token = value.trim().toUpperCase()
  if (TIERS.includes(token as RiskTier)) return token as RiskTier
  return LEGACY_COLOUR_ALIASES[token] ?? null
}

const TIER_LABEL: Record<RiskTier, string> = {
  HIGH_RISK: 'High risk',
  MEDIUM_RISK: 'Medium risk',
  LOW_RISK: 'Low risk',
}

/**
 * The ONE place a tier becomes user-visible copy (ADR 0026). Every dashboard/export/config label
 * routes through here so the vocabulary can never drift back to colour or severity words.
 * Re-exported from `components/intelligence/RiskBadge` for UI call sites.
 */
export function tierLabel(tier: RiskTier | null): string {
  return tier ? TIER_LABEL[tier] : 'Not assessed'
}

/**
 * A tier as a standalone phrase. Assessed tiers already name the axis ("High risk"); the null
 * state needs the noun prefixed so "Not assessed" is unambiguous when read alone.
 */
export function riskHeadline(tier: RiskTier | null): string {
  return tier == null ? 'Risk: Not assessed' : tierLabel(tier)
}

const TIER_COLOR: Record<RiskTier, RiskColorName> = {
  HIGH_RISK: 'red',
  MEDIUM_RISK: 'amber',
  LOW_RISK: 'green',
}

/**
 * The ONE tier → colour mapping (ADR 0026). CSS classes, chart palettes and dot colours derive
 * their colour word from here; no other module may compare a tier against 'red'/'amber'/'green'.
 */
export function tierColorName(tier: RiskTier | null): RiskColorName {
  return tier == null ? 'unassessed' : TIER_COLOR[tier]
}

/** Map a semantic-layer contract's risk fields into {@link ContractRisk} (all fields null-safe). */
export function riskFromSemantic(raw: SemanticRiskFields): ContractRisk {
  return {
    tier: asTier(raw.riskTier),
    reasons: (raw.riskReasons ?? []).filter((r): r is string => typeof r === 'string' && r.length > 0),
    provenance: {
      baselineTier: asTier(raw.riskBaselineTier),
      decidedBy: raw.riskDecidedBy === 'POLICY' ? 'POLICY' : raw.riskDecidedBy === 'BASELINE' ? 'BASELINE' : null,
      justification: raw.riskJustification ?? null,
      evaluatedAt: raw.riskEvaluatedAt ?? null,
    },
  }
}

/** The semantic-layer properties that HOLD a tier — the columns a generic renderer must badge. */
const TIER_COLUMNS = new Set(['risktier', 'riskbaselinetier'])

/**
 * True when a query result column carries a risk TIER (as opposed to a score, reasons, or the
 * deciding layer). Config-driven dashboards project these straight out of the semantic layer, and a
 * generic table would otherwise print the stored token verbatim; this is how {@link RiskBadge}
 * gets routed in without a per-dashboard column type (ADR 0026: tiers are never raw text on screen).
 * Case- and separator-insensitive so `riskTier` / `risk_tier` / a custom alias all match.
 */
export function isRiskTierColumn(column: string): boolean {
  return TIER_COLUMNS.has(column.replace(/[_-]/g, '').toLowerCase())
}

/**
 * Any contract-ish record the dashboards count risk over: either the mapped summary shape
 * (`risk: RiskTier | null`, e.g. `ClientContractSummary`) or the raw semantic shape
 * (`riskTier: string | null`, straight off the fold read).
 */
export type RiskBearing = SemanticRiskFields & { risk?: RiskTier | null }

/**
 * True when a contract has no assessed tier — the ONE definition of "Not assessed" (ADR 0023 /
 * risk-tier-switchover D4: never conflate it with LOW_RISK). Handles both shapes above so the
 * "Assess risk (N unassessed)" tallies on the client / employment / lease dashboards can never
 * disagree, and so an off-vocabulary token counts as unassessed rather than being guessed at.
 */
export function isUnassessed(contract: RiskBearing): boolean {
  if (contract.risk !== undefined) return asTier(contract.risk) == null
  return riskFromSemantic(contract).tier == null
}

/** True when the customer's risk policy moved this tier off its baseline. */
export function isPolicyOverride(provenance: RiskProvenance): boolean {
  return provenance.decidedBy === 'POLICY'
}

const EXPOSURE: Record<RiskTier, number> = { LOW_RISK: 0, MEDIUM_RISK: 1, HIGH_RISK: 2 }

/**
 * True when the policy DOWNGRADED the tier below its baseline — the case that must render
 * loudest (a wrong downgrade hides real exposure behind a Low risk badge).
 */
export function isPolicyDowngrade(tier: RiskTier | null, provenance: RiskProvenance): boolean {
  return isPolicyOverride(provenance)
    && tier != null && provenance.baselineTier != null
    && EXPOSURE[tier] < EXPOSURE[provenance.baselineTier]
}

/** What a policy override is, as pure data — the shape both the badge and the detail modal say. */
export interface RiskOverride {
  /** The policy moved the tier off its baseline (an affirmation is not an override). */
  override: boolean
  /** The move was DOWNWARD — the case that hides real exposure behind a calmer badge. */
  downgrade: boolean
  /** The verb to use: 'downgraded' or 'escalated'. Always populated, only meaningful when `override`. */
  overrideWord: 'downgraded' | 'escalated'
}

/**
 * The ONE reading of a tier's provenance (ADR 0023: a policy move — especially a downgrade — is
 * never visually silent). Extracted here rather than left inside `riskBadgePresentation` because
 * the badge and the risk detail modal must word the same override identically; two copies of this
 * is how the hover and the modal start disagreeing about what the policy did.
 */
export function riskOverrideOf(
  tier: RiskTier | null,
  provenance: RiskProvenance | null | undefined,
): RiskOverride {
  const override = provenance != null && isPolicyOverride(provenance)
  const downgrade = provenance != null && isPolicyDowngrade(tier, provenance)
  return { override, downgrade, overrideWord: downgrade ? 'downgraded' : 'escalated' }
}

/**
 * Sort rank, riskiest first: HIGH_RISK < MEDIUM_RISK < LOW_RISK < not-assessed. Not-assessed
 * sorts last — unknown is not "safe", but a table ordered by risk should lead with known
 * exposure.
 */
export function riskTierRank(tier: RiskTier | null): number {
  return tier == null ? TIERS.length : TIERS.indexOf(tier)
}
